import React from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  useTheme,
} from '@mui/material';
import { panelSurfaceSx, getTechnicianDisplayName } from './controlCenterUtils';
import ControlCenterInfoTooltip from './ControlCenterInfoTooltip';

/** User-facing explanations aligned with controlCenterDao.getTechnicians. */
export const TECHNICIANS_INFO = {
  total:
    'Active technicians company-wide. Not limited by the date or client filters.',
  required1099:
    'Active technicians with paid earnings over $2,000 in the current calendar year (not the page date range). Not limited by the client filter.',
  needW9:
    'Active technicians without a W9 marked. Not limited by the date or client filters.',
  blacklisted:
    'All blacklisted technicians, including inactive. Not limited by the date or client filters.',
  assignedJob:
    'Most recent pipeline assignment in the selected date range. Uses the technician’s schedule date when set, otherwise the job’s create date. Client filter applies here.',
};

const LabelWithInfo = ({ label, info, sx }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 0,
      ...sx,
    }}
  >
    <Typography component="span" sx={{ fontSize: 'inherit', color: 'inherit', fontWeight: 'inherit' }}>
      {label}
    </Typography>
    {info ? <ControlCenterInfoTooltip title={info} /> : null}
  </Box>
);

const Stat = ({ label, value, info }) => (
  <Box sx={{ minWidth: 0, flex: 1 }}>
    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
      <LabelWithInfo label={label} info={info} />
    </Typography>
    <Typography sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 600 }}>{value}</Typography>
  </Box>
);

export default function TechniciansPanel({
  data,
  loading,
  error,
  rangeLabel,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const summary = data?.summary;
  const technicians = data?.technicians || [];

  return (
    <Box sx={panelSurfaceSx(theme)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          px: { xs: 1.5, sm: 2.5 },
          pt: { xs: 1.5, sm: 2 },
          pb: 1,
        }}
      >
        <Typography sx={{ fontSize: 15, fontWeight: 500, color: 'text.secondary' }}>
          Technicians
        </Typography>
        {rangeLabel && (
          <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>{rangeLabel}</Typography>
        )}
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ px: 2, pb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {!loading && !error && (
        <Box sx={{ px: { xs: 1.5, sm: 2.5 }, pb: { xs: 1.5, sm: 2 } }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: { xs: 2, sm: 4 }, mb: 2 }}>
            <Stat
              label="Total technicians"
              value={summary?.total ?? 0}
              info={TECHNICIANS_INFO.total}
            />
            <Stat
              label="1099 Required"
              value={summary?.required1099 ?? 0}
              info={TECHNICIANS_INFO.required1099}
            />
            <Stat
              label="Form W9 Needed"
              value={summary?.needW9 ?? 0}
              info={TECHNICIANS_INFO.needW9}
            />
            <Stat
              label="Blacklisted"
              value={summary?.blacklisted ?? 0}
              info={TECHNICIANS_INFO.blacklisted}
            />
          </Box>

          <Box sx={{ maxHeight: 320, overflow: 'auto' }}>
            {technicians.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No technicians found
              </Typography>
            ) : (
              technicians.map((tech, index) => {
                const name = getTechnicianDisplayName(tech);
                const assigned = tech.assignedJob;
                return (
                  <Box
                    key={tech.id}
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1.25,
                      borderTop: '1px solid',
                      borderColor: isDark ? '#334155' : '#e7e7e7',
                    }}
                  >
                    <Avatar sx={{ width: 36, height: 36, fontSize: 14 }}>
                      {(name || '?').charAt(0)}
                    </Avatar>
                    <Box sx={{ minWidth: 140, flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{name || '—'}</Typography>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        {tech.phone || 'No phone'}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 180, flex: 1.2 }}>
                      <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                        <LabelWithInfo
                          label="Assigned Job"
                          info={index === 0 ? TECHNICIANS_INFO.assignedJob : null}
                        />
                      </Typography>
                      {assigned ? (
                        <>
                          <Typography sx={{ fontSize: 13 }} noWrap>
                            {assigned.clientName || '—'}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                            {assigned.address || '—'}
                          </Typography>
                        </>
                      ) : (
                        <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>
                          None
                        </Typography>
                      )}
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
}
